import { Button } from '@app/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { RouteErrorComponent } from '../components/route-error';
import { BrandLink } from '../components/BrandLink';
import { SiteFooter } from '../components/site-footer';

export const Route = createFileRoute('/privacy')({
  component: Privacy,
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
});

function Privacy() {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b">
        <BrandLink />
        <div className="flex gap-3">
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 py-16 px-6">
        <div className="max-w-3xl mx-auto prose prose-neutral dark:prose-invert">
          <h1>Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: February 10, 2026</p>
          <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
            DRAFT - Pending legal review
          </p>

          <h2>1. Information We Collect</h2>
          <p>
            We collect information you provide directly to us, including your name, email address,
            phone number, university affiliation, and NIL deal information (brand names, contract
            terms, payment amounts, and deadlines).
          </p>

          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>
              Send you notifications about your deals, including payment reminders and deadline
              alerts via email and SMS
            </li>
            <li>
              Process transactions and send related information including purchase confirmations
            </li>
            <li>
              Provide AI-powered features such as contract summarization, deal valuation, and
              screenshot data extraction
            </li>
            <li>Respond to your comments, questions, and customer service requests</li>
          </ul>

          <h2>3. Third-Party Services</h2>
          <p>
            We use the following third-party services to operate NilBoard. Each service has its own
            privacy policy governing its use of your data:
          </p>
          <ul>
            <li>
              <strong>Stripe</strong> - Payment processing. Stripe collects payment information
              directly. See{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                Stripe Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Twilio</strong> - SMS notifications. Your phone number is shared with Twilio
              to deliver text messages. See{' '}
              <a
                href="https://www.twilio.com/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Twilio Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Google</strong> - Authentication (Sign in with Google). See{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Anthropic</strong> - AI-powered features (contract summaries, deal valuation).
              Deal data sent to Anthropic's API for processing. See{' '}
              <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">
                Anthropic Privacy Policy
              </a>
              .
            </li>
          </ul>

          <h2>4. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed
            to provide you services. You may request deletion of your account and associated data at
            any time by contacting us at{' '}
            <a href="mailto:support@nilboard.app">support@nilboard.app</a>.
          </p>

          <h2>5. Data Security</h2>
          <p>
            We use industry-standard security measures to protect your data, including encryption in
            transit (TLS) and at rest (AES-256), row-level access controls, and audit logging.
          </p>

          <h2>6. FERPA Notice</h2>
          <p>
            NilBoard may process data related to college athletes that could be subject to the
            Family Educational Rights and Privacy Act (FERPA) when used in connection with
            university compliance programs. We maintain appropriate safeguards and will enter into
            data processing agreements with university partners as required.
          </p>

          <h2>7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Opt out of SMS notifications at any time by texting STOP</li>
            <li>Withdraw consent for data processing</li>
          </ul>

          <h2>8. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our data practices, please
            contact us at: <a href="mailto:support@nilboard.app">support@nilboard.app</a>
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
